import type {
  DecryptMessageBatchParams,
  SignatureRefreshHooks,
} from '../../services/messageDecryptionPipeline';
import {
  findAndDecryptSignedPreKey,
  findAndDecryptOneTimePreKey,
  deleteOneTimePreKey,
  storeSessionKey,
  getPersistedSessionKey,
  deletePersistedSessionKey,
} from '../../services/preKeyStorage';
import { notifyOtpkConsumed } from '../../services/preKeyService';
import type { LoadedDecryptKeys } from './conversationDecryptKeys';

export type DecryptMessageBatchSharedFields = Omit<
  DecryptMessageBatchParams,
  'messages' | 'conversationId' | 'pagingCursor' | 'existingMessages'
>;

/**
 * Shared `decryptMessageBatch` arguments used by conversation message fetch paths.
 * Call sites supply `messages`, `conversationId`, `pagingCursor`, and `existingMessages`.
 */
export function buildDecryptMessageBatchSharedFields(args: {
  identityId: string;
  wrappingKey: Uint8Array | null;
  keys: LoadedDecryptKeys;
  signingKeyCache: Record<string, string>;
  pqSigningKeyCache: Record<string, string>;
  sessionKeyCache: Map<string, Uint8Array>;
  metadataKeyCache?: Map<string, Uint8Array>;
  api: {
    identity: {
      getPublicKeys: (id: string) => Promise<{
        data?: { signingPublicKey?: string; pqSigningPublicKey?: string } | null;
      }>;
    };
  };
  resolveParticipants: (ids: string[]) => void | Promise<Record<string, unknown>>;
  signatureHooks?: SignatureRefreshHooks;
}): DecryptMessageBatchSharedFields {
  const {
    identityId,
    wrappingKey,
    keys: { ecdhPrivateKey, kemPrivateKey, myRoutingTag },
    signingKeyCache,
    pqSigningKeyCache,
    sessionKeyCache,
    api,
    resolveParticipants,
    metadataKeyCache,
    signatureHooks,
  } = args;

  return {
    ...signatureHooks,
    identityId,
    wrappingKey,
    ecdhPrivateKey,
    kemPrivateKey,
    myRoutingTag,
    signingKeyCache,
    pqSigningKeyCache,
    sessionKeyCache,
    getConversationMetadataKey: metadataKeyCache
      ? (conversationId) => metadataKeyCache.get(conversationId)
      : undefined,
    fetchSigningKey: async (sid) => {
      try {
        const keysResp = await api.identity.getPublicKeys(sid);
        if (keysResp.data?.signingPublicKey) {
          if (!pqSigningKeyCache[sid]) {
            pqSigningKeyCache[sid] = keysResp.data.pqSigningPublicKey ?? '';
          }
          return keysResp.data.signingPublicKey;
        }
      } catch (err) {
        console.warn('[Conversations] decrypt: failed to fetch signing key for', sid, err);
      }
      return null;
    },
    resolveParticipants: (ids) => {
      void resolveParticipants(ids);
    },
    findAndDecryptSignedPreKey,
    findAndDecryptOneTimePreKey,
    deleteOneTimePreKey,
    getPersistedSessionKey,
    storeSessionKey,
    deletePersistedSessionKey,
    notifyOtpkConsumed,
  };
}
